import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { HeroChicago } from "@/components/site/HeroChicago";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { ValueProps } from "@/components/site/ValueProps";
import { Footer } from "@/components/site/Footer";
import { WhyChooseChicago } from "@/components/site/WhyChooseChicago";
import { ChicagoClinicGuide } from "@/components/site/ChicagoClinicGuide";
import eddieOlczyk from "@/assets/restore/eddie-olczyk.png.asset.json";
import { doctors } from "@/lib/site-data";
import { CHICAGO_META, CHICAGO_FAQ, CHICAGO_COPY, CHICAGO_GUARANTEE, CHICAGO_PRICING, CHICAGO_SURGEONS } from "@/lib/wp-restore/cities";
import { safeJsonLd } from "@/lib/structured-data";
import { getChicagoStructuredData } from "@/lib/structured-data/chicago";
import { useLocalQuoteLinks } from "@/hooks/use-local-quote-links";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const NoShaveFuture = lazy(() => import("@/components/site/NoShaveFuture").then(m => ({ default: m.NoShaveFuture })));
const NoShaveWhy = lazy(() => import("@/components/site/NoShaveWhy").then(m => ({ default: m.NoShaveWhy })));
const Guarantee = lazy(() => import("@/components/site/Guarantee").then(m => ({ default: m.Guarantee })));
const Pricing = lazy(() => import("@/components/site/Pricing").then(m => ({ default: m.Pricing })));
const DoctorBio = lazy(() => import("@/components/site/DoctorBio").then(m => ({ default: m.DoctorBio })));
const Surgeons = lazy(() => import("@/components/site/Surgeons").then(m => ({ default: m.Surgeons })));
const DoctorsGrid = lazy(() => import("@/components/site/DoctorsGrid").then(m => ({ default: m.DoctorsGrid })));
const LocationsMapChicago = lazy(() => import("@/components/site/LocationsMapChicago").then(m => ({ default: m.LocationsMapChicago })));
const VirtualConsult = lazy(() => import("@/components/site/VirtualConsult").then(m => ({ default: m.VirtualConsult })));
const Faq = lazy(() => import("@/components/site/Faq").then(m => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/locations/restore-chicago")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/restore-chicago/" }],
    meta: [
      { title: CHICAGO_META.title },
      { name: "description", content: CHICAGO_META.description },
      { property: "og:title", content: "Get Your Hair Back | RESTORE® Hair Transplants — Chicago" },
      {
        property: "og:description",
        content:
          "RESTORE® Hair transplants in Chicago and the surrounding suburbs. Trusted by Chicago athletes and broadcasters for natural, undetectable results.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd({ "@graph": getChicagoStructuredData() }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  useLocalQuoteLinks();
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <HeroChicago eyebrowTag="h1" headingTag="h2" subheadingTag="h2" />
        <NorwoodQuiz />
        <WhyChooseChicago />
        <Suspense fallback={null}>
          <div className="hidden md:block">
            <Results showDisclaimer nameTag="h2" />
          </div>
          <ValueProps wpCopy={CHICAGO_COPY} hideEyebrowMobile />
          <NoShaveFuture wpCopy={CHICAGO_COPY.future} />
          <NoShaveWhy dougLeft wpCopy={CHICAGO_COPY.why} />
          <Reviews ssrMore description="RESTORE has changed the lives of thousands of men struggling with hair loss. No wonder our clients from all over Chicagoland trust RESTORE as their #1 choice when getting a hair transplant. Here's what they have to say about their results." />
          <Guarantee wpCopy={CHICAGO_GUARANTEE} />
          <Pricing wpCopy={CHICAGO_PRICING} imageSrc={eddieOlczyk.url} imageAlt="Eddie Olczyk, U.S. Hockey Hall of Fame & RESTORE Client" hidePlayOverlay />
          <DoctorBio />
          <Surgeons eyebrowTag="h3" eyebrow={CHICAGO_SURGEONS.eyebrow} heading={<>A World Renowned <span className="text-foreground">Team</span></>} />
          <DoctorsGrid nameTag="h2" doctors={doctors.filter((d) => ["Dr. James Harris", "Dr. Adam Cloninger"].includes(d.name))} />
          <LocationsMapChicago />
          <ChicagoClinicGuide />
          <Faq items={CHICAGO_FAQ} numbered headingTag="h3" questionTag="h4" />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}